import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { GENDER_LIST_CONFIGS, Patient } from 'app/core/models';
import { SHOW_PATIENT_DATE_OF_BIRTH } from 'app/core/constants';
import { RxHelper } from 'app/core/services';
import { minYearsValidator, noUrlValidator, noWhitespaceValidator } from 'app/core/form-validators';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { CheckboxModule } from 'primeng/checkbox';
import { PatientFileModel, PatientFileType } from 'app/core/models';
import { PatientFilesUploadComponent } from './patient-files-upload.component';
import { PatientFileUploadState } from './patient-file-upload-state';

const MIN_YEARS_AGO = 3;

@Component({
  selector: 'wl-patient-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RadioButton,
    DatePicker,
    DatePickerModule,
    FloatLabel,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    Toast,
    CheckboxModule,
    PatientFilesUploadComponent,
  ],
  templateUrl: './patient-edit.component.html',
  styleUrl: './patient-edit.component.scss',
  providers: [RxHelper],
})
export class PatientEditComponent implements OnChanges, OnInit {
  @Output() readonly formChanged = new EventEmitter<FormGroup | null>();
  @Output() readonly patientFilesChanged = new EventEmitter<PatientFileUploadState | null>();
  @Input() defaults?: Partial<Patient> = {};
  @Input() patientFiles: PatientFileModel[] | null = null;
  @Input() showMomentDirect = false;
  @Input() lockMomentDirect = false;
  readonly genderList = GENDER_LIST_CONFIGS;
  readonly showDateOfBirth = SHOW_PATIENT_DATE_OF_BIRTH;

  currentDate: Date = new Date();
  readonly yearRange = `1900:${this.currentDate.getFullYear()}`;

  form: FormGroup = new FormGroup({});
  isMomentDirect = false;

  private readonly _translate = inject(TranslateService);

  private readonly _messageService = inject(MessageService);

  constructor(
    private fb: FormBuilder,
    private readonly _rxHelper: RxHelper
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      gender: [this.defaults?.gender || '', [Validators.required, Validators.maxLength(500)]],
      firstName: [
        this.defaults?.firstName || '',
        [Validators.required, Validators.maxLength(500), noUrlValidator(), noWhitespaceValidator()],
      ],
      lastName: [
        this.defaults?.lastName || '',
        [Validators.required, Validators.maxLength(500), noUrlValidator(), noWhitespaceValidator()],
      ],
      dateOfBirth: [this.getDefaultDateOfBirth(), [minYearsValidator(MIN_YEARS_AGO)]],
      isMomentDirect: [this.getDefaultMomentDirect()],
      notes: [this.defaults?.notes || '', [Validators.maxLength(500)]],
    });
    this.form.valueChanges.pipe(this._rxHelper.autoUnsubscribe).subscribe(() => {
      this.emitFormChanged();
    });

    const birthdayControl = this.form.get('dateOfBirth');

    birthdayControl?.valueChanges.pipe(this._rxHelper.autoUnsubscribe).subscribe(() => {
      if (birthdayControl?.hasError('minYearsAgo')) {
        this.sendWarnMessage();
      }
    });

    const isMomentDirectControl = this.form.get('isMomentDirect');
    isMomentDirectControl?.valueChanges.pipe(this._rxHelper.autoUnsubscribe).subscribe((value: boolean) => {
      if (this.lockMomentDirect && !value) {
        isMomentDirectControl.setValue(true, { emitEvent: false });
        value = true;
      }
      this.isMomentDirect = this.showMomentDirect && value;
      this.patientFilesChanged.emit(this.isMomentDirect ? this.createEmptyPatientFileState() : null);
      this.emitFormChanged();
    });

    this.updateMomentDirectState(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.form?.get('dateOfBirth') ||
      (!changes['defaults'] && !changes['showMomentDirect'] && !changes['lockMomentDirect'])
    ) {
      return;
    }

    this.form.patchValue({
      gender: this.defaults?.gender || '',
      firstName: this.defaults?.firstName || '',
      lastName: this.defaults?.lastName || '',
      dateOfBirth: this.getDefaultDateOfBirth(),
      isMomentDirect: this.getDefaultMomentDirect(),
      notes: this.defaults?.notes || '',
    });
    this.updateMomentDirectState(true);
  }

  onPatientFilesChanged(state: PatientFileUploadState): void {
    this.patientFilesChanged.emit(state);
    this.emitFormChanged();
  }

  private emitFormChanged() {
    this.formChanged.emit(this.form.valid ? this.form : null);
  }

  private getDefaultMomentDirect(): boolean {
    if (!this.showMomentDirect) {
      return false;
    }

    return this.lockMomentDirect ? true : !!this.defaults?.isMomentDirect;
  }

  private getDefaultDateOfBirth() {
    const dateOfBirth = this.defaults?.dateOfBirth;
    if (!dateOfBirth) {
      return null;
    }

    const date = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private updateMomentDirectState(emitFileState: boolean): void {
    this.isMomentDirect = this.showMomentDirect && !!this.form.get('isMomentDirect')?.value;
    if (emitFileState) {
      this.patientFilesChanged.emit(this.isMomentDirect ? this.createEmptyPatientFileState() : null);
    }
    this.emitFormChanged();
  }

  private sendWarnMessage() {
    this._messageService.add({
      severity: 'warn',
      summary: this._translate.instant('general.warning'),
      detail: this._translate.instant('messages.patientAgeMinimumRequired', { years: MIN_YEARS_AGO }),
    });
  }

  private createEmptyPatientFileState(): PatientFileUploadState {
    return {
      filesByCategory: {
        [PatientFileType.UpperScan]: [],
        [PatientFileType.LowerScan]: [],
        [PatientFileType.BiteScan]: [],
        [PatientFileType.PatientSmiling]: [],
      },
      removedFileIds: [],
      isValid: false,
    };
  }
}
